import { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  TextField, 
  Button, 
  Box, 
  Paper,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  Stack,
  styled
} from '@mui/material';
import { doc, getDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db, firebaseConfig } from '../../firebase/config';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import { getAuth, deleteUser, signInWithEmailAndPassword } from 'firebase/auth';
import { initializeApp } from 'firebase/app';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { authService } from '../../services/authService';

// Initialiser une deuxième instance de Firebase pour la suppression
const secondaryApp = initializeApp(firebaseConfig, 'Secondary');
const secondaryAuth = getAuth(secondaryApp);

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

function EditEtudiant() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [newCompetence, setNewCompetence] = useState('');
  const [previewImage, setPreviewImage] = useState('');
  
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    formation: '',
    specialite: '',
    annee: '',
    description: '',
    competences: [],
    linkedin: '',
    github: '',
    photo: ''
  });

  useEffect(() => {
    const fetchEtudiant = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/etudiants/${id}`, {
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        });

        if (!response.ok) {
          throw new Error("Impossible de charger l'étudiant");
        }

        const data = await response.json();
        const etudiant = data.etudiant;
        
        setFormData({
          nom: etudiant.user.nom,
          prenom: etudiant.user.prenom,
          email: etudiant.user.email,
          formation: etudiant.formation || '',
          specialite: etudiant.specialite || '',
          annee: etudiant.annee || '',
          description: etudiant.description || '',
          competences: etudiant.competences?.map(comp => ({
            id_comp: comp.id_comp,
            nom_comp: comp.nom_comp
          })) || [],
          linkedin: etudiant.linkedin || '',
          github: etudiant.github || '',
          photo: etudiant.photo || ''
        });
        setPreviewImage(etudiant.photo || '');
      } catch (err) {
        console.error("Erreur lors du chargement de l'étudiant:", err);
        setError("Impossible de charger les données de l'étudiant");
      } finally {
        setLoading(false);
      }
    };

    fetchEtudiant();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddCompetence = () => {
    if (newCompetence.trim()) {
      const newCompObj = {
        id_comp: null,  // sera généré par le backend
        nom_comp: newCompetence.trim()
      };
      
      // Vérifier si la compétence n'existe pas déjà
      if (!formData.competences.some(comp => comp.nom_comp === newCompObj.nom_comp)) {
        setFormData(prev => ({
          ...prev,
          competences: [...prev.competences, newCompObj]
        }));
        setNewCompetence('');
      }
    }
  };

  const handleDeleteCompetence = (competenceToDelete) => {
    const updatedCompetences = formData.competences.filter(
      comp => comp.nom_comp !== competenceToDelete.nom_comp
    );

    setFormData(prev => ({
      ...prev,
      competences: updatedCompetences
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Données pour la mise à jour de l'étudiant
      const etudiantDataToSubmit = {
        user: {
          nom: formData.nom,
          prenom: formData.prenom,
          email: formData.email
        },
        formation: formData.formation || undefined,
        annee: formData.annee || undefined,
        description: formData.description || undefined,
        specialite: formData.specialite || undefined,
        github: formData.github || undefined,
        linkedin: formData.linkedin || undefined,
        photo: formData.photo || undefined
      };

      // Supprimer les champs undefined
      Object.keys(etudiantDataToSubmit).forEach(key => 
        etudiantDataToSubmit[key] === undefined && delete etudiantDataToSubmit[key]
      );

      // Mise à jour des informations de l'étudiant
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/etudiants/${id}`, {
        method: 'PUT',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify(etudiantDataToSubmit)
      });

      if (!response.ok) {
        throw new Error("Erreur lors de la modification de l'étudiant");
      }

      // Mise à jour des compétences
      const competencesResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/etudiants/${id}/competences`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify({
          competences: formData.competences.map(comp => comp.nom_comp)
        })
      });

      if (!competencesResponse.ok) {
        throw new Error('Erreur lors de la mise à jour des compétences');
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/etudiants');
      }, 2000);
    } catch (err) {
      console.error("Erreur lors de la modification de l'étudiant:", err);
      setError(err.message || "Une erreur est survenue lors de la modification");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    setError('');
    setDeleteError('');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/etudiants/${id}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });

      if (!response.ok) {
        throw new Error("Erreur lors de la suppression de l'étudiant");
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/etudiants');
      }, 2000);
    } catch (err) {
      console.error("Erreur lors de la suppression de l'étudiant:", err);
      setError("Une erreur est survenue lors de la suppression");
    } finally {
      setLoading(false);
      setOpenDeleteDialog(false);
      setDeletePassword('');
    }
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress color="primary" />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/etudiants')}
        variant="contained"
        color="primary"
        sx={{ mb: 4 }}
      >
        Retour aux étudiants
      </Button>

      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Modifier l'étudiant
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Modifications enregistrées avec succès! Redirection...
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
          <TextField
            required
            fullWidth
            label="Nom"
            name="nom"
            value={formData.nom}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          <TextField
            required
            fullWidth
            label="Prénom"
            name="prenom"
            value={formData.prenom}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          <TextField
            required
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          <TextField
            required
            fullWidth
            label="Formation"
            name="formation"
            value={formData.formation}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          <TextField
            required
            fullWidth
            label="Spécialité"
            name="specialite"
            value={formData.specialite}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          <TextField
            required
            fullWidth
            label="Année"
            name="annee"
            value={formData.annee}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          <TextField
            required
            fullWidth
            label="Description"
            name="description"
            multiline
            rows={4}
            value={formData.description}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                fullWidth
                label="Ajouter une compétence"
                value={newCompetence}
                onChange={(e) => setNewCompetence(e.target.value)}
              />
              <Button
                onClick={handleAddCompetence}
                variant="contained"
                color="primary"
              >
                Ajouter
              </Button>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {formData.competences.map((comp, index) => (
                <Chip
                  key={index}
                  label={comp.nom_comp}
                  onDelete={() => handleDeleteCompetence(comp)}
                  color="primary"
                  sx={{ m: 0.5 }}
                />
              ))}
            </Stack>
          </Box>

          <TextField
            fullWidth
            label="LinkedIn URL"
            name="linkedin"
            value={formData.linkedin}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="GitHub URL"
            name="github"
            value={formData.github}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          <Box sx={{ mb: 3 }}>
            {previewImage && (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Photo actuelle :
                </Typography>
                <Box
                  component="img"
                  src={previewImage}
                  alt="Photo de l'étudiant"
                  sx={{
                    maxWidth: '200px',
                    maxHeight: '200px',
                    objectFit: 'cover',
                    borderRadius: 1
                  }}
                />
              </Box>
            )}

            {!previewImage && (
              <Typography variant="subtitle2" color="text.secondary">
                Aucune photo disponible
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between', mt: 4 }}>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setOpenDeleteDialog(true)}
              disabled={loading}
            >
              Supprimer l'étudiant
            </Button>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/etudiants')}
                disabled={loading}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                color="primary"
              >
                {loading ? 'Modification...' : "Modifier l'étudiant"}
              </Button>
            </Box>
          </Box>
        </Box>

        <Dialog
          open={openDeleteDialog}
          onClose={() => {
            setOpenDeleteDialog(false);
            setDeletePassword('');
            setDeleteError('');
          }}
        >
          <DialogTitle>
            Confirmer la suppression
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Êtes-vous sûr de vouloir supprimer cet étudiant ? Cette action est irréversible.
            </DialogContentText>
            <TextField
              fullWidth
              label="Mot de passe de l'étudiant"
              type="password"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              error={!!deleteError}
              helperText={deleteError}
              required
            />
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => {
                setOpenDeleteDialog(false);
                setDeletePassword('');
                setDeleteError('');
              }}
              disabled={loading}
            >
              Annuler
            </Button>
            <Button 
              onClick={handleDelete}
              color="error"
              disabled={loading || !deletePassword}
              variant="contained"
            >
              {loading ? 'Suppression...' : 'Supprimer'}
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Container>
  );
}

export default EditEtudiant; 